import type { DropdownListOption } from '@lightspeed/unified-react-components'
import { useT } from '@transifex/react'
import { ApiOutlet } from '@vend/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRegisterManager } from '../../../../../../ng-compat/hooks'
import { outletsStore } from '../../../../../../factories/database/outlets-store'
import { useAppSelector } from '../../../../../../state/hooks'
import { selectSaleLocalId } from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { getAvailabilityByOutletId } from '../../../../../../util/cross-location-fulfilment'
import { OutletOption, OutletSelectProps } from '../OutletSelectTypes'
import { OutletSummary } from '../components/OutletSummary'

const OUTLET_SEARCH_MIN_CHARS = 2

const getOutletAddress = (outlet: ApiOutlet): string =>
  [
    outlet.physical_address_1,
    outlet.physical_address_2,
    outlet.physical_suburb,
    outlet.physical_city,
    outlet.physical_postcode,
    outlet.physical_state,
  ]
    .filter(Boolean)
    .join(', ')

const toOutletOption = (
  outlet: ApiOutlet,
  availabilityByOutletId?: Map<string, number>
): OutletOption => {
  const option: OutletOption = {
    id: outlet.id,
    name: outlet.name,
    address: getOutletAddress(outlet),
  }

  if (availabilityByOutletId) {
    option.available = availabilityByOutletId.get(outlet.id) ?? 0
  }

  return option
}

const sortOutlets = (
  outlets: readonly OutletOption[],
  activeOutletId?: string
) => {
  const activeOutlet = outlets.find(outlet => outlet.id === activeOutletId)

  if (!activeOutlet) {
    return [...outlets].sort((a, b) => a.name.localeCompare(b.name))
  }

  const otherOutlets = outlets
    .filter(outlet => outlet.id !== activeOutletId)
    .sort((a, b) => a.name.localeCompare(b.name))

  return [activeOutlet, ...otherOutlets]
}

type LoadOutletOptionsParams = {
  registerManager: ReturnType<typeof useRegisterManager>
  activeOutletId?: string
  productId?: string
  saleLocalId?: string
  includeAvailability: boolean
  isStale?: () => boolean
}

const loadOutletOptions = async ({
  registerManager,
  activeOutletId,
  productId,
  saleLocalId,
  includeAvailability,
  isStale = () => false,
}: LoadOutletOptionsParams): Promise<OutletOption[]> => {
  const allOutlets = await outletsStore.all()
  const eligibleOutlets = registerManager.getEligibleCLFTargetOutlets(
    allOutlets
  )
  const outletIds = eligibleOutlets.map(outlet => outlet.id)

  const availabilityByOutletId =
    includeAvailability && productId && outletIds.length > 0 && !isStale()
      ? await getAvailabilityByOutletId(productId, outletIds, saleLocalId)
      : undefined

  return sortOutlets(
    eligibleOutlets.map(outlet =>
      toOutletOption(outlet, availabilityByOutletId)
    ),
    activeOutletId
  )
}

const resolveSelectedOutletId = (
  options: readonly OutletOption[],
  currentOutletId: string,
  activeOutletId?: string
): string =>
  options.some(outlet => outlet.id === currentOutletId)
    ? currentOutletId
    : options.find(outlet => outlet.id === activeOutletId)?.id ??
      options[0]?.id ??
      ''

export const useOutletSelect = ({
  initialSelectedOutletId,
  productId,
  includeAvailability = false,
  disabled = false,
  onOutletChange,
  onSelectedOutletIdChange,
}: OutletSelectProps = {}) => {
  const t = useT()
  const registerManager = useRegisterManager()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const [outletOptions, setOutletOptions] = useState<OutletOption[]>([])
  const [isOutletOptionsLoading, setIsOutletOptionsLoading] = useState(true)
  const [selectedOutletId, setSelectedOutletId] = useState(
    initialSelectedOutletId || ''
  )
  const [searchTerm, setSearchTerm] = useState('')
  const activeOutletId = registerManager.getActiveOutlet()?.id

  useEffect(() => {
    let isStale = false

    setIsOutletOptionsLoading(true)

    loadOutletOptions({
      registerManager,
      activeOutletId,
      productId,
      saleLocalId,
      includeAvailability,
      isStale: () => isStale,
    })
      .catch((): OutletOption[] => [])
      .then(options => {
        if (isStale) {
          return
        }

        setOutletOptions(options)
        setSelectedOutletId(currentOutletId =>
          resolveSelectedOutletId(options, currentOutletId, activeOutletId)
        )
        setIsOutletOptionsLoading(false)
      })

    return () => {
      isStale = true
    }
  }, [
    activeOutletId,
    includeAvailability,
    productId,
    registerManager,
    saleLocalId,
  ])

  useEffect(() => {
    if (
      initialSelectedOutletId &&
      outletOptions.some(outlet => outlet.id === initialSelectedOutletId)
    ) {
      setSelectedOutletId(initialSelectedOutletId)
    }
  }, [initialSelectedOutletId, outletOptions])

  const emptyLabel = t('No outlets available', {
    _context: 'Message displayed when there are no outlets available.',
  })
  const noResultsLabel = t('No outlets found', {
    _context: 'Message displayed when no outlets match the search term.',
  })
  const loadingLabel = t('Loading outlets', {
    _context: 'Message displayed when outlets are loading.',
  })

  const selectedOutlet =
    outletOptions.find(outlet => outlet.id === selectedOutletId) ??
    outletOptions.find(outlet => outlet.id === activeOutletId) ??
    outletOptions[0]
  const isEmpty = outletOptions.length === 0
  const isDisabled = disabled || isOutletOptionsLoading || isEmpty
  const triggerOutlet = selectedOutlet ?? {
    id: '',
    name: isOutletOptionsLoading ? loadingLabel : emptyLabel,
  }

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const hasActiveSearch = normalizedSearch.length >= OUTLET_SEARCH_MIN_CHARS
  const filteredOutlets = useMemo(() => {
    if (!hasActiveSearch) {
      return outletOptions
    }

    const isNameMatch = (outlet: OutletOption) =>
      outlet.name.toLowerCase().includes(normalizedSearch)
    const isAddressMatch = (outlet: OutletOption) =>
      (outlet.address ?? '').toLowerCase().includes(normalizedSearch)

    // Name matches rank above address-only matches.
    return [
      ...outletOptions.filter(isNameMatch),
      ...outletOptions.filter(
        outlet => !isNameMatch(outlet) && isAddressMatch(outlet)
      ),
    ]
  }, [hasActiveSearch, normalizedSearch, outletOptions])

  const dropdownItems = useMemo(
    () =>
      filteredOutlets.length > 0
        ? [
            {
              items: filteredOutlets.map(outlet => ({
                label: outlet.name,
                value: outlet.id,
                contentSlot: (
                  <OutletSummary
                    outlet={outlet}
                    isListItem
                    showAvailability={!isDisabled}
                  />
                ),
              })),
            },
          ]
        : [],
    [filteredOutlets, isDisabled]
  )

  const [highlightedOutletId, setHighlightedOutletId] = useState<string | null>(
    null
  )

  useEffect(() => {
    setHighlightedOutletId(
      hasActiveSearch
        ? filteredOutlets[0]?.id ?? null
        : selectedOutletId || null
    )
  }, [filteredOutlets, hasActiveSearch, searchTerm, selectedOutletId])

  const handleHighlightChange = useCallback(
    ({ highlightedValue }: { highlightedValue: string | null }) => {
      setHighlightedOutletId(highlightedValue)
    },
    []
  )

  const outletById = useMemo(
    () => new Map(outletOptions.map(outlet => [outlet.id, outlet])),
    [outletOptions]
  )

  const handleOutletSelect = (item: DropdownListOption) => {
    if (isDisabled) {
      return
    }

    const outlet = outletById.get(item.value)

    if (outlet) {
      setSelectedOutletId(outlet.id)
      setSearchTerm('')
      onOutletChange?.(outlet)
    }
  }

  const shouldShowOutletHint =
    !!activeOutletId && !!selectedOutlet && selectedOutlet.id !== activeOutletId

  useEffect(() => {
    if (isOutletOptionsLoading) {
      return
    }

    onSelectedOutletIdChange?.(selectedOutlet?.id)
  }, [isOutletOptionsLoading, onSelectedOutletIdChange, selectedOutlet?.id])

  const shouldRender = isOutletOptionsLoading || outletOptions.length > 0

  return {
    isOutletOptionsLoading,
    selectedOutletId,
    filteredOutlets,
    dropdownItems,
    highlightedOutletId,
    handleHighlightChange,
    handleOutletSelect,
    isDisabled,
    isEmpty,
    hasActiveSearch,
    searchTerm,
    setSearchTerm,
    shouldShowOutletHint,
    triggerOutlet,
    emptyLabel,
    noResultsLabel,
    shouldRender,
  }
}
